/* globals d3, topojson */
import * as d3 from 'd3';
import * as topojson from 'topojson';
import moment from 'moment';

import TrackingAnalytics from 'app/collections/tracking_analytics';
import DateRange from 'app/models/date_range';
import DropdownSelection from 'app/models/dropdown_selection';
import DynamicDropdownSelection from 'app/models/dynamic_dropdown_selection';

import Base from 'app/views/base';
import DynamicSelectorView from 'app/views/dynamic_selector_view';
import LoaderView from 'app/views/loader';
import SelectorView from 'app/views/selector_view';

import tmpl from 'templates/carrier_activity_map';

export default Base.extend({
  compiledTemplate: tmpl,
  className: 'carrier-activity-map',

  initialize() {
    this.dateRange = new DateRange({
      startDatetime: moment.utc().subtract(7, 'days').startOf('day'),
      endDatetime: moment.utc().startOf('day'),
    });

    this.carrierSelection = new DropdownSelection({
      choices: [
        { option: 'usps', readable: 'USPS' },
        { option: 'fedex', readable: 'FedEx' },
        { option: 'ups', readable: 'UPS' },
      ],
      paramKey: 'carrier',
    });

    this.facilityTypeSelection = new DynamicDropdownSelection({
      dropdowns: {
        usps: new DropdownSelection({
          choices: [
            { option: 'top', readable: 'Top Facilities by Volume' },
            { option: 'isc', readable: 'International Sorting Center' },
            { option: 'scf', readable: 'Sectional Center Facility' },
          ],
        }),
        ups: new DropdownSelection({
          choices: [
            { option: 'top', readable: 'Top Facilities by Volume' },
            { option: 'airport_hub', readable: 'Airport Hub' },
            { option: 'hub', readable: 'Hub' },
            { option: 'center', readable: 'Regional Center' },
          ],
        }),
        fedex: new DropdownSelection({
          choices: [
            { option: 'top', readable: 'Top Facilities by Volume' },
            { option: 'hub', readable: 'Hub' },
            { option: 'center', readable: 'Regional Center' },
            { option: 'terminal', readable: 'Terminal' },
          ],
        }),
      },
      paramKey: 'type',
      dependentSelection: this.carrierSelection,
    });

    this.collection = new TrackingAnalytics([], {
      dateRange: this.dateRange,
      carrierSelection: this.carrierSelection,
      facilityTypeSelection: this.facilityTypeSelection,
    });

    this.addSubview('carrierPicker', '#carrier_picker', new SelectorView({
      model: this.carrierSelection,
    }));
    this.addSubview('facilityTypePicker', '#facility_type_picker', new DynamicSelectorView({
      model: this.facilityTypeSelection, dependentSelection: this.carrierSelection,
    }));
    this.addSubview('trackerLoader', '#tracker_loader', new LoaderView({
      collection: this.collection, type: 'flat',
    }));

    this.collection.getFirstPage();
  },

  onDomRefresh() {
    this.facilityChart.init(this.$('.facility-chart'));
    this.facilitySelector.init(this.$('.facility-selector'), this.facilityChart, this.collection);
    this.usMap.init(this.$('.us-map'), this.collection, this.facilitySelector);

    this.collection.on('sync', this.usMap.renderCities, this.usMap);
    this.collection.on('sync', this.facilitySelector.loadInitialZip, this.facilitySelector);
  },

  usMap: {
    init($selected, collection, facilitySelector) {
      this.data = collection;
      this.facilitySelector = facilitySelector;

      const d3DataPath = '/assets/d3_data/';
      const usTopoJsonFile = `${d3DataPath}us.json`; // bl.ocks.org/mbostock/raw/4090846/us.json

      const $container = d3.select($selected.get(0));
      this.width = $selected.width();
      this.height = this.width * 0.62;

      this.circleRadius = 2.5;

      this.hoverDiv = $container
        .append('div')
        .attr('class', 'tooltip')
        .style('opacity', 0);

      this.svg = $container.append('svg')
        .attr('width', this.width)
        .attr('height', this.height);

      this.projection = d3.geoAlbersUsa()
        .translate([this.width / 2, this.height / 2])
        .scale(this.width * 1.3);

      const path = d3.geoPath()
        .projection(this.projection);

      /* Zoom and Pan */
      this.zoom = d3.zoom()
        .scaleExtent([1, 40])
        .translateExtent([[0, 0], [this.width, this.height]]);

      const ready = (err, usData) => {
        if (err) throw err;

        this.svg.append('g')
          .selectAll('path')
          .data(topojson.feature(usData, usData.objects.states).features)
          .enter()
          .append('path')
          .attr('d', path)
          .attr('class', 'state');
        this.circleGroup = this.svg.append('g');
      };

      d3.queue()
        .defer(d3.json, usTopoJsonFile)
        .await(ready);
    },

    renderCities() {
      this.svg.call(this.zoom.transform, d3.zoomIdentity); // Reset Zoom

      let circles = this.circleGroup.selectAll('circle').data(
        this.data.models.filter(d => !!this.projection([d.get('longitude'), d.get('latitude')])),
        d => `${d.get('latitude')},${d.get('longitude')}` // key for identifying circles
      );
      circles.exit().remove();
      circles = circles.enter()
        .append('circle')
        .attr('r', this.circleRadius)
        .attr('class', 'zip-circle')
      .merge(circles)
        .attr('cx', (d) => {
          const lon = d.get('longitude');
          const lat = d.get('latitude');
          const projection = this.projection([lon, lat]);
          if (projection) return projection[0];
          return undefined;
        })
        .attr('cy', (d) => {
          const lon = d.get('longitude');
          const lat = d.get('latitude');
          const projection = this.projection([lon, lat]);
          if (projection) return projection[1];
          return undefined;
        })

        /* Hover Events */
        .on('mouseover', () => {
          this.hoverDiv.transition()
            .duration(200)
            .style('opacity', 0.90);
        })
        .on('mousemove', (d) => {
          const mouse = d3.mouse(this.svg.node()).map(n => parseInt(n, 10));
          this.hoverDiv.style('left', `${mouse[0] + 15}px`)
            .style('top', `${mouse[1] - 35}px`)
            .text(`Zip: ${d.get('postal_code')}XX, ${d.get('state_code')}`);

          this.facilitySelector.render(d.get('facilities'));
        })
        .on('mouseout', () => {
          this.hoverDiv.transition()
            .duration(200)
            .style('opacity', 0);
        });

      /* Zoom and Pan */
      this.zoom.on('zoom', () => {
        this.svg.selectAll('g').attr('transform', d3.event.transform);
        circles.attr('r', () => this.circleRadius / d3.event.transform.k);
      });
      this.svg.call(this.zoom);
    },
  },

  facilityChart: {
    init($selected) {
      this.container = d3.select($selected.get(0));
      this.container.style('opacity', 0);
      this.width = $selected.width();
      this.height = this.width * 0.62;
      this.margin = { top: 30, right: 20, bottom: 30, left: 50 };
      this.transitionDuration = 500;
      this.delayDuration = this.transitionDuration / 2;

      const barPadding = 0.2;
      const axisTicks = { qty: 5, outerSize: 0 };

      this.title = this.container.append('h4').attr('class', 'title');

      this.legend = this.container.append('div').attr('class', 'legend');
      this.legend.append('div').attr('class', 'arrivals').text('Arrivals');
      this.legend.append('div').attr('class', 'departures').text('Departures');

      this.svg = this.container
         .append('svg')
         .attr('width', this.width)
         .attr('height', this.height)
         .append('g')
         .attr('transform', `translate(${this.margin.left},${this.margin.top})`);

      // https://bl.ocks.org/mbostock/3887051
      // https://bl.ocks.org/mbostock/3943967
      // https://bl.ocks.org/shimizu/a4c0c940b19d42cf8ebca29e20573aca
      this.xScale0 = d3.scaleBand()
                       .range([0, this.width - this.margin.left - this.margin.right])
                       .padding(barPadding);

      this.xScale1 = d3.scaleBand();
      this.yScale = d3.scaleLinear().range([this.height - this.margin.top - this.margin.bottom, 0]);

      this.xAxis = d3.axisBottom(this.xScale0).tickSizeOuter(axisTicks.outerSize);
      this.yAxis = d3.axisLeft(this.yScale).ticks(axisTicks.qty).tickSizeOuter(axisTicks.outerSize);

      // Add the X Axis
      this.svg.append('g')
         .attr('class', 'x axis')
         .attr('transform', `translate(0,${this.height - this.margin.top - this.margin.bottom})`);
         /* rotate labels */
         // .selectAll('text')
         // .attr('y', 0)
         // .attr('x', 9)
         // .attr('transform', 'rotate(90)')
         // .style('text-anchor', 'start');

      // Add the Y Axis
      this.svg.append('g')
         .attr('class', 'y axis');
    },

    render(facility) {
      this.container.style('opacity', 1);
      const data = facility.daily_stats.map(d => ({
        date: d.date,
        events: [
          { type: 'arrivals', val: parseInt(d.arrivals, 10) },
          { type: 'departures', val: parseInt(d.departures, 10) },
        ],
      }));

      this.title.text(facility.name);

      this.xScale0.domain(data.map(d => d.date));
      this.xScale1.domain(['arrivals', 'departures']).range([0, this.xScale0.bandwidth()]);
      this.yScale.domain([0, d3.max(data, d => d3.max(d.events, n => n.val))]);

      this.svg.select('.x.axis')
        .transition()
        .duration(this.transitionDuration)
        .call(this.xAxis);
      this.svg.select('.y.axis')
        .transition()
        .duration(this.transitionDuration)
        .call(this.yAxis);

      let date = this.svg.selectAll('.date')
        .data(data, d => d.date);
      date.exit().remove();
      date = date
        .enter()
        .append('g')
        .attr('class', 'date')
      .merge(date)
        .attr('transform', d => `translate(${this.xScale0(d.date)},0)`);

      let events = date.selectAll('.bar')
        .data(d => d.events);
      events.exit().remove();
      events = events
        .enter()
        .append('rect')
        .attr('class', d => `bar ${d.type}`)
        .attr('x', d => this.xScale1(d.type))
        .attr('width', this.xScale1.bandwidth())
        .attr('y', this.yScale(0))
        .attr('height', 0)
      .merge(events)
        .transition()
        .duration(this.transitionDuration)
        .delay((d, i) => i * this.delayDuration)
        .attr('y', d => this.yScale(d.val))
        .attr('height', d => (
          this.height - this.margin.top - this.margin.bottom - this.yScale(d.val)
        ));

      return events;
    },
  },

  facilitySelector: {
    init($selected, facilityChart, collection) {
      this.collection = collection;
      this.facilityChart = facilityChart;

      this.container = d3.select($selected.get(0));
      this.container.style('opacity', 0);
      this.tbody = this.container.select('tbody');
    },

    render(facilities) {
      this.container.style('opacity', 1);
      this.facilityChart.render(facilities[0]);

      // https://bl.ocks.org/boeric/e16ad218bc241dfd2d6e
      let tr = this.tbody.selectAll('tr').data(facilities, d => d.facility_uuid);
      tr.exit().remove();
      tr = tr.enter().append('tr');

      tr = tr.selectAll('td')
        .data(d => [d])
        .enter()
      .merge(tr)
        .append('td')
        .text(d => d.name)
        .on('mousemove', d => this.facilityChart.render(d));
    },

    loadInitialZip() {
      const randIdx = Math.floor(Math.random() * this.collection.length);
      this.render(this.collection.at(randIdx).get('facilities'));
    },
  },
});



// WEBPACK FOOTER //
// ./app/views/carrier_activity_map_page.js